import styles from "./SpecialtyReport.module.css";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { TabView, TabPanel } from "primereact/tabview";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useMemo, useState, useEffect, useRef } from "react";
import * as client from "../../client";
import { motion } from "framer-motion";

// Component imports
import { Conversation } from "./Conversation";
import { LayersCard } from "./layers/LayersCard";
import { LossRunCard } from "./loss-run/LossRunCard";
import { AddressCard } from "./sov/AddressCard";
import { ReportActionMenu } from "./dashboard/ReportActionMenu";
import { StatusIcon } from "./dashboard/StatusIcon";
import { PantheonAvatar } from "../../components/avatar/Avatar";
import { PrintableReport } from "./PrintableReport";
import { CompanyInfoHeader } from "./CompanyInfoHeader";
import { AppetiteModal } from "./appetite/AppetiteModal";
import { AppetiteIcon } from "./appetite/AppetiteIcon";
import { FinancialCard } from "./financial/FinancialCard";
import { CrimeCard } from "./crime/CrimeCard";
import { PolicyCard } from "./policy/PolicyCard";
import { NewsCard } from "./news/NewsCard";
import { SummaryCard } from "./summary/SummaryCard";

// Utilities
import { useLocalUser, useReport, useUserById } from "./data";
import {
  appetiteScoreForReportInfo,
  TEMP_PIPELINE_EXAMPLES,
} from "./dashboard/util";
import { displayNameForUser, sleepMs } from "../../util";
import { ROUTES } from "../../routes";
import { ReferralCard } from "./referral/ReferralCard";
import { applyOverridesToResponse, SafeReportResponse } from "./Conversion";
import { LossControlReports } from "./LossControlReports";
import { MapModalForSubmission } from "./map/MapModal";
import { SomeClientsOnly } from "../../dev";
import { GeneralInput } from "./general-input/GeneralInput";
import { GoogleSheetsModal } from "./sov/GoogleSheetsModal";
import { useLogUIEventCallback, useLogUIEventOnMount } from "../../amplitude";
import { AttachmentCard } from "./attachments/AttachmentCard";
import { Toast } from "primereact/toast";
import { FloatingFeedbackButton } from "../supahub/FloatingFeedbackButton";
import {
  fakeCompanyNameFromCompanyName,
  usePrivacyMode,
} from "../../context-providers/Privacy";

interface RoutedTabViewProps {
  rawSpecialtyPropertyInfo: client.ReportResponse;
  safeSpecialtyPropertyInfo: SafeReportResponse;
  isCasualty: boolean;
  setMapModalVisible: (visible: boolean) => void;
  clientId: string;
}

// Type for tab configuration
interface TabConfig {
  header: string;
  path: string;
  casualtyOnly?: boolean;
  showIf?: (
    info: SafeReportResponse,
    clientId: string,
    username?: string
  ) => boolean;
  component:
    | { type: "safe"; component: React.ComponentType<SafeTabComponentProps> }
    | { type: "raw"; component: React.ComponentType<RawTabComponentProps> }
    | {
        type: "safe_and_raw";
        component: React.ComponentType<SafeAndRawComponentProps>;
      };
}

// Common props that might be passed to tab components
interface CommonTabComponentProps {
  forPrint?: boolean;
  isCasualty?: boolean;
  setMapVisible?: (visible: boolean) => void;
}

type RawTabComponentProps = CommonTabComponentProps & {
  specialtyPropertyInfo: client.ReportResponse;
};

type SafeTabComponentProps = CommonTabComponentProps & {
  specialtyPropertyInfo: SafeReportResponse;
};

type SafeAndRawComponentProps = CommonTabComponentProps & {
  specialtyPropertyInfo: SafeReportResponse;
  rawSpecialtyPropertyInfo: client.ReportResponse;
};

// Define all possible tabs with their conditions
const ALL_TABS: TabConfig[] = [
  {
    header: "Review",
    path: "review",
    component: { type: "safe_and_raw", component: SummaryCard },
  },
  {
    header: "Underwriting",
    path: "underwriting",
    component: { type: "safe", component: ReferralCard },
  },
  {
    header: "General",
    path: "general-input",
    component: { type: "raw", component: GeneralInput },
  },
  {
    header: "Finances",
    path: "finances",
    casualtyOnly: true,
    component: { type: "safe", component: FinancialCard },
  },
  {
    header: "Crime",
    path: "crime",
    casualtyOnly: true,
    component: { type: "safe", component: CrimeCard },
  },
  {
    header: "Layers",
    path: "layers",
    component: { type: "safe", component: LayersCard },
    showIf: (_, clientId) => clientId === "pantheon",
  },
  {
    header: "SOV",
    path: "sov",
    component: { type: "raw", component: AddressCard },
  },
  {
    header: "Loss Run",
    path: "loss-run",
    component: { type: "safe", component: LossRunCard },
    showIf: (_, clientId, username) =>
      clientId === "pantheon" || username === "pantheon_test_user",
  },
  {
    header: "Loss Control Reports",
    path: "loss-control-reports",
    component: { type: "safe", component: LossControlReports },
    showIf: (_, clientId) => clientId === "pantheon" || clientId === "canopius",
  },
  {
    header: "Policy Information",
    path: "policy",
    showIf: (info) => !!info.report_json.deductible_info,
    component: { type: "safe", component: PolicyCard },
  },
  {
    header: "Attachments",
    path: "attachments",
    component: { type: "safe", component: AttachmentCard },
  },
  {
    header: "Company News",
    path: "news",
    component: { type: "safe", component: NewsCard },
    showIf: (_, clientId) => clientId === "pantheon" || clientId === "canopius",
  },
];

const RoutedTabView: React.FC<RoutedTabViewProps> = ({
  safeSpecialtyPropertyInfo,
  rawSpecialtyPropertyInfo,
  isCasualty,
  setMapModalVisible,
  clientId,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { tabPath } = useParams<{ tabPath?: string }>();
  const logUIEvent = useLogUIEventCallback();
  const localUser = useLocalUser();

  // Filter tabs based on conditions
  const availableTabs = ALL_TABS.filter((tab) => {
    if (tab.casualtyOnly && !isCasualty) return false;
    if (
      tab.showIf &&
      !tab.showIf(safeSpecialtyPropertyInfo, clientId, localUser?.email)
    )
      return false;
    return true;
  });

  // Get the current tab from the URL
  const getCurrentTabIndex = () => {
    const index = availableTabs.findIndex((tab) => tab.path === tabPath);
    return index >= 0 ? index : 0;
  };

  // Handle tab changes
  const handleTabChange = (e: { index: number }) => {
    const newPath = availableTabs[e.index].path;
    logUIEvent("acropolis_click_report_tab", { tab_path: newPath });
    navigate(
      ROUTES.DASHBOARD_REPORT(safeSpecialtyPropertyInfo.report_id, newPath)
    );
  };

  // Set initial tab on mount
  useEffect(() => {
    const currentPath = location.pathname.split("/").pop();
    if (!currentPath || currentPath === safeSpecialtyPropertyInfo.report_id) {
      navigate(
        ROUTES.DASHBOARD_REPORT(
          safeSpecialtyPropertyInfo.report_id,
          availableTabs[0].path
        )
      );
    }
  }, [location.pathname, safeSpecialtyPropertyInfo.report_id]);

  return (
    <TabView
      className={styles.tabView}
      activeIndex={getCurrentTabIndex()}
      onTabChange={handleTabChange}
    >
      {availableTabs.map((tab) => {
        const setMapVisible =
          tab.path === "sov" ? setMapModalVisible : undefined;
        return (
          <TabPanel key={tab.path} header={tab.header}>
            {tab.component.type === "safe" ? (
              <tab.component.component
                specialtyPropertyInfo={safeSpecialtyPropertyInfo}
                forPrint={false}
                isCasualty={tab.path === "sov" ? isCasualty : undefined}
                setMapVisible={setMapVisible}
              />
            ) : (
              <>
                {tab.component.type === "safe_and_raw" ? (
                  <tab.component.component
                    rawSpecialtyPropertyInfo={rawSpecialtyPropertyInfo}
                    specialtyPropertyInfo={safeSpecialtyPropertyInfo}
                    forPrint={false}
                    isCasualty={tab.path === "sov" ? isCasualty : undefined}
                    setMapVisible={setMapVisible}
                  />
                ) : (
                  <tab.component.component
                    specialtyPropertyInfo={rawSpecialtyPropertyInfo}
                    forPrint={false}
                    isCasualty={tab.path === "sov" ? isCasualty : undefined}
                    setMapVisible={setMapVisible}
                  />
                )}
              </>
            )}
          </TabPanel>
        );
      })}
    </TabView>
  );
};

const LoadedReport: React.FC<{
  specialtyPropertyInfo: client.ReportResponse;
  safeResponse: SafeReportResponse;
  clientId: string;
}> = ({ specialtyPropertyInfo, safeResponse, clientId }) => {
  useLogUIEventOnMount("acropolis_show_report");

  const [isMapModalVisible, setIsMapModalVisible] = useState(false);
  const [appetiteModalVisible, setAppetiteModalVisible] = useState(false);
  const [googleSheetsModalVisible, setGoogleSheetsModalVisible] =
    useState(false);
  const [isPrinting, setIsPrinting] = useState(false);

  // TODO: use a flag on the report
  const isCasualty =
    safeResponse?.report_json.company_info.company_name === "Lake Forest II";

  const assignee = useUserById(specialtyPropertyInfo?.assignee);
  const handler = useUserById(
    specialtyPropertyInfo.additional_assignees?.assignees["handler"]
  );

  useEffect(() => {
    (async () => {
      if (isPrinting) {
        await sleepMs(0);
        window.print();
        await sleepMs(0);
        setIsPrinting(false);
      }
    })();
  }, [isPrinting]);

  const toast = useRef<Toast>(null);

  useEffect(() => {
    if (specialtyPropertyInfo.report_json.sovs.length === 0) {
      toast.current?.show({
        severity: "warn",
        summary: "No SOV Found",
        detail:
          'No SOV files were found when processing this submission. If you think an SOV was included, please classify it on the "Attachments" tab.',
        sticky: true,
      });
    }
  }, [specialtyPropertyInfo.report_json.sovs.length]);

  const { privacyModeEnabled } = usePrivacyMode();

  return (
    <>
      <Toast ref={toast} />
      {isPrinting ? (
        <PrintableReport
          specialtyPropertyInfo={safeResponse}
          rawSpecialtyPropertyInfo={specialtyPropertyInfo}
        />
      ) : null}
      <AppetiteModal
        report={specialtyPropertyInfo.report_json}
        visible={appetiteModalVisible}
        onHide={() => setAppetiteModalVisible(false)}
      />
      <div className={styles.container}>
        <div className={styles.verticalContainer}>
          <motion.div
            initial={{ opacity: 0, x: -16 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.35, ease: "backOut" }}
          >
            <Card
              title={
                privacyModeEnabled
                  ? fakeCompanyNameFromCompanyName(
                      safeResponse.report_json.company_info.company_name
                    )
                  : safeResponse.report_json.company_info.company_name
              }
              subTitle={
                <div className={styles.statusContainer}>
                  <div className={styles.roundedHeaderPiece}>
                    <div className={styles.assigneeContainer}>
                      <PantheonAvatar
                        label={displayNameForUser(assignee)}
                        showName={true}
                      />
                    </div>
                  </div>
                  <div className={styles.roundedHeaderPiece}>
                    <div className={styles.assigneeContainer}>
                      <PantheonAvatar
                        label={displayNameForUser(handler)}
                        showName={true}
                      />
                    </div>
                  </div>
                  <div className={styles.roundedHeaderPiece}>
                    <StatusIcon status={specialtyPropertyInfo.status} />
                  </div>
                  <div className={styles.roundedHeaderPiece}>
                    <AppetiteIcon
                      naicsInfo={specialtyPropertyInfo.report_json.naics_info}
                      onClick={() => setAppetiteModalVisible(true)}
                      size="full_width"
                      appetite={appetiteScoreForReportInfo(
                        specialtyPropertyInfo.report_json
                          .business_classification,
                        specialtyPropertyInfo.report_json.appetite_score
                      )}
                    />
                  </div>
                </div>
              }
              style={{ position: "relative" }}
            >
              <CompanyInfoHeader specialtyPropertyInfo={safeResponse} />
              <span>
                {safeResponse.report_json.company_info.company_description}
              </span>
              <div className={styles.topRightButtons}>
                <Button
                  className={styles.textButton}
                  onClick={() => setGoogleSheetsModalVisible(true)}
                  severity="secondary"
                  text
                  icon="pi pi-google"
                  tooltip="Send info to Google Sheets"
                  tooltipOptions={{ position: "bottom" }}
                />
                {/*
                <Button
                  className={styles.textButton}
                  onClick={() => setIsPrinting(true)}
                  severity="secondary"
                  text
                  icon="pi pi-download"
                  tooltip="Print report"
                  tooltipOptions={{ position: "bottom" }}
                />*/}
                <ReportActionMenu
                  report={{
                    report_metadata: {
                      ...safeResponse.report_json,
                      // Unused here, so we can just mock it
                      sov_metadata: { num_locations: 0, total_tiv: 0 },
                      lob: safeResponse.lob,
                    },
                    ...specialtyPropertyInfo,
                  }}
                  isUpcomingRenewal={TEMP_PIPELINE_EXAMPLES.has(
                    safeResponse.report_json.company_info.company_name
                  )}
                />
              </div>
            </Card>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: -16 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.35, ease: "backOut", delay: 0.07 }}
          >
            <RoutedTabView
              rawSpecialtyPropertyInfo={specialtyPropertyInfo}
              safeSpecialtyPropertyInfo={safeResponse}
              isCasualty={isCasualty}
              setMapModalVisible={setIsMapModalVisible}
              clientId={clientId}
            />
          </motion.div>
          {isMapModalVisible && (
            <MapModalForSubmission
              visible={isMapModalVisible}
              onHide={() => setIsMapModalVisible(false)}
              specialtyPropertyInfo={safeResponse}
            />
          )}
          <GoogleSheetsModal
            visible={googleSheetsModalVisible}
            setVisible={setGoogleSheetsModalVisible}
            report={safeResponse}
            sendGeneralInput={true}
            sendAddresses={true}
          />
        </div>
        <SomeClientsOnly clientIds={["pantheon", "canopius"]}>
          <Conversation specialtyPropertyInfo={specialtyPropertyInfo} />
        </SomeClientsOnly>
      </div>
      <FloatingFeedbackButton />
    </>
  );
};

export const SpecialtyReport: React.FC<{ clientId: string }> = ({
  clientId,
}: {
  clientId: string;
}) => {
  const { reportId } = useParams<{ reportId: string }>();

  const reportResponse = useReport(reportId);
  const specialtyPropertyInfo = reportResponse.isSuccess
    ? reportResponse.data
    : undefined;

  const safeResponse = useMemo(
    () =>
      specialtyPropertyInfo
        ? applyOverridesToResponse(specialtyPropertyInfo)
        : undefined,
    [specialtyPropertyInfo]
  );

  if (reportId === undefined) {
    // This should never happen
    return <></>;
  }

  // TODO: return some sort of failure state if we couldn't find the report
  if (specialtyPropertyInfo === undefined || safeResponse === undefined) {
    return <></>;
  }

  return (
    <LoadedReport
      safeResponse={safeResponse}
      specialtyPropertyInfo={specialtyPropertyInfo}
      clientId={clientId}
      key={reportId}
    />
  );
};
